import React, { useState, useEffect, useCallback } from "react"
import { useLogout, useGetIdentity } from "@refinedev/core"
import { useLocation, useNavigate } from "react-router-dom"
import { AppSidebar } from "./AppSidebar"
import { Toaster } from "@/components/ui/toaster"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Moon, Sun, Maximize2, LogOut, Bell, Search, CheckCheck, ChevronDown, ChevronRight, Home, UserCircle, Settings, HelpCircle } from "lucide-react"
import { helpTopicForPath } from "@/help/routeMap"
import { AppSwitcher } from "@/components/AppSwitcher/AppSwitcher"

interface AppLayoutProps {
  children: React.ReactNode
  title?: React.ReactNode
}

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem("labboxx-dark-mode")
    return stored === "true"
  })

  useEffect(() => {
    const root = document.documentElement
    if (isDark) {
      root.classList.add("dark")
    } else {
      root.classList.remove("dark")
    }
    localStorage.setItem("labboxx-dark-mode", String(isDark))
  }, [isDark])

  const toggle = useCallback(() => setIsDark((prev) => !prev), [])

  return { isDark, toggle }
}

export function AppLayout({ children, title }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [notificationCount] = useState(0)
  const { isDark, toggle: toggleDarkMode } = useDarkMode()
  const { mutate: logout } = useLogout()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: identity } = useGetIdentity<{ name?: string; email?: string }>()

  const userName = identity?.name || identity?.email?.split('@')[0] || 'User'
  const userEmail = identity?.email || 'user@labboxx.com'
  const getInitials = () => {
    const parts = userName.split(' ')
    return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U'
  }

  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const breadcrumbs: Array<{ name: string; path: string }> = []
    let currentPath = ''
    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`
      const formattedName = segment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      breadcrumbs.push({ name: formattedName, path: currentPath })
    })
    return breadcrumbs
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <TooltipProvider>
      <div className="flex h-screen overflow-hidden bg-background">
        <AppSidebar
          title={title}
          collapsed={collapsed}
          onCollapse={setCollapsed}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="flex h-14 shrink-0 items-center justify-between border-b bg-background px-4">
            {/* Left Section - User & Breadcrumbs */}
            <div className="flex items-center gap-4">
              {/* User Avatar Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 hover:bg-muted rounded-lg p-2 transition-colors">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left hidden sm:block">
                      <div className="text-sm font-medium text-foreground">{userName}</div>
                      <div className="text-xs text-muted-foreground">{userEmail}</div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/core/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout()} className="text-destructive-700">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Divider */}
              <div className="h-8 w-px bg-border" />

              {/* Breadcrumbs */}
              <nav className="flex items-center gap-2 text-sm">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="p-1.5 rounded hover:bg-muted text-muted-foreground"
                >
                  <Home className="h-4 w-4" />
                </button>
                {breadcrumbs.map((crumb, index) => (
                  <div key={crumb.path} className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    <button
                      onClick={() => navigate(crumb.path)}
                      className={
                        index === breadcrumbs.length - 1
                          ? "px-2 py-1 rounded text-foreground font-medium"
                          : "px-2 py-1 rounded hover:bg-muted text-muted-foreground transition-colors"
                      }
                    >
                      {crumb.name}
                    </button>
                  </div>
                ))}
              </nav>
            </div>

            {/* Right Section - Search & Actions */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  className="pl-9 w-64 h-9 bg-card border-border"
                />
              </div>

              {/* App Switcher (9-dot) */}
              <AppSwitcher currentProductSlug="labboxx" />

              {/* LabBOXX-InTutor — context-aware help */}
              <button
                onClick={() => navigate(`/help/${helpTopicForPath(location.pathname)}`)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                title="LabBOXX-InTutor — manual, tutorials & glossary"
              >
                <HelpCircle className="h-5 w-5 text-muted-foreground" />
              </button>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 rounded-lg hover:bg-muted transition-colors">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    {notificationCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-destructive text-destructive-foreground text-xs">
                        {notificationCount > 99 ? "99+" : notificationCount}
                      </Badge>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-3 py-2">
                    <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                    {notificationCount > 0 && (
                      <button className="text-xs text-primary hover:underline flex items-center gap-1">
                        <CheckCheck className="h-3 w-3" /> Mark all read
                      </button>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No notifications
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Dark Mode */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    onClick={toggleDarkMode}
                  >
                    {isDark ? (
                      <Sun className="h-5 w-5 text-warning-700" />
                    ) : (
                      <Moon className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {isDark ? "Light Mode" : "Dark Mode"}
                </TooltipContent>
              </Tooltip>

              {/* Fullscreen */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    onClick={() => {
                      if (document.fullscreenElement) {
                        document.exitFullscreen()
                      } else {
                        document.documentElement.requestFullscreen()
                      }
                    }}
                  >
                    <Maximize2 className="h-5 w-5 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Fullscreen</TooltipContent>
              </Tooltip>

              {/* User Profile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 rounded-lg hover:bg-muted transition-colors">
                    <UserCircle className="h-5 w-5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{userName}</span>
                      <span className="text-xs font-normal text-muted-foreground">{userEmail}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/core/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout()} className="text-destructive-700">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Main content */}
          {/* `app-canvas` paints the three brand-derived washes behind every
              page. It goes on <main> rather than on <body> because the shell
              paints its own bg-background over the body on every screen — a
              wash on the body would never be seen. */}
          <main className="flex-1 overflow-auto app-canvas">
            <div className="p-4 md:p-6">{children}</div>
          </main>
        </div>
        <Toaster />
      </div>
    </TooltipProvider>
  )
}
